import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { api } from '../lib/api'
import { CATALOG_LINE, CATALOG_SORT, catalogSortByLine } from '../config/catalog.js'

// ── Context ───────────────────────────────────────────────────────

const ProductsContext = createContext(null)

export function ProductsProvider({ children }) {
  const [products, setProducts]               = useState([])
  const [catalogSettings, setCatalogSettings] = useState({
    global_sale: { active: false, label: '' },
    featured_product_ids: [],
  })
  const [inventory, setInventory] = useState({})
  const [loading, setLoading] = useState(true)
  const [error, setError]     = useState(null)
  const [previewOverride, setPreviewOverride] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [prods, settings, inv] = await Promise.all([
        api.getProducts(),
        api.getCatalogSettings(),
        api.getInventory(),
      ])
      setProducts(Array.isArray(prods) ? prods : [])
      setCatalogSettings(settings ?? {})
      setInventory(inv ?? {})
    } catch (e) {
      setError(e.message ?? 'Failed to load products.')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { load() }, [load])

  // Site Content admin preview bridge: the admin panel embeds an iframe of
  // this same app and posts draft (unsaved) site_content edits into it so
  // changes show with real styling before "Publish" is hit. Gated on
  // actually being framed, so this is a complete no-op on every real visit.
  useEffect(() => {
    if (window.self === window.top) return
    function onMessage(e) {
      if (e.origin !== window.location.origin) return
      if (e.data?.type === 'lookslab-preview-site-content') setPreviewOverride(e.data.payload)
    }
    window.addEventListener('message', onMessage)
    return () => window.removeEventListener('message', onMessage)
  }, [])

  // ── Derived helpers ───────────────────────────────────────────

  const globalSale          = catalogSettings.global_sale ?? { active: false, label: '' }
  const featuredProductIds  = catalogSettings.featured_product_ids ?? []
  const hallOfFame          = catalogSettings.hall_of_fame ?? []
  const faqs                = catalogSettings.faqs ?? []
  const checkoutConfig      = catalogSettings.checkout_config ?? null
  const siteContent         = { ...(catalogSettings.site_content ?? {}), ...(previewOverride ?? {}) }
  const branding            = catalogSettings.branding ?? {}
  const seoSettings         = catalogSettings.seo ?? {}
  const preorderMessage     = catalogSettings.preorder_message ?? 'This order contains one or more pre-order items. These will not ship until stock arrives — estimated wait is 2–3 weeks. Your invoice and payment process remain the same.'
  const customerAccounts    = catalogSettings.customer_accounts ?? { enabled: true, allowRegistration: true, features: { rewards: true, referrals: true, wishlist: true } }
  const rewardsConfig       = catalogSettings.rewards_config ?? null

  const getProductById = useCallback(
    (id) => products.find((p) => p.id === id) ?? null,
    [products],
  )

  const getProductsByCatalogLine = useCallback(
    (line) => {
      if (line === CATALOG_LINE.ALL) return [...products]
      const want = line === CATALOG_LINE.BEAUTY ? CATALOG_LINE.BEAUTY : CATALOG_LINE.VIALS
      return products.filter((p) => (p.catalogLine ?? CATALOG_LINE.VIALS) === want)
    },
    [products],
  )

  const getCatalogProductsForLine = useCallback(
    (line) => {
      const list = getProductsByCatalogLine(line)
      const mode = catalogSortByLine[line] ?? CATALOG_SORT.CONFIG
      if (mode === CATALOG_SORT.ALPHA) {
        return [...list].sort((a, b) => a.name.localeCompare(b.name, undefined, { sensitivity: 'base' }))
      }
      return list
    },
    [getProductsByCatalogLine],
  )

  const getFeaturedProducts = useCallback(
    () => featuredProductIds.map((id) => getProductById(id)).filter(Boolean),
    [featuredProductIds, getProductById],
  )

  // ── Sale / stock helpers (depend on globalSale) ───────────────

  const isSaleActive = useCallback(
    () => Boolean(globalSale?.active),
    [globalSale],
  )

  const saleBadgeLabel = useCallback(
    () => {
      if (!isSaleActive()) return null
      return globalSale.label?.trim() || 'Sale'
    },
    [globalSale, isSaleActive],
  )

  const showCompareAtPrice = useCallback(
    (variant) => isSaleActive() && variant?.compareAtPrice != null && variant.compareAtPrice > variant.price,
    [isSaleActive],
  )

  const value = useMemo(() => ({
    products,
    catalogSettings,
    globalSale,
    featuredProductIds,
    hallOfFame,
    faqs,
    checkoutConfig,
    siteContent,
    branding,
    seoSettings,
    inventory,
    preorderMessage,
    customerAccounts,
    rewardsConfig,
    loading,
    error,
    reload: load,
    getProductById,
    getProductsByCatalogLine,
    getCatalogProductsForLine,
    getFeaturedProducts,
    isSaleActive,
    saleBadgeLabel,
    showCompareAtPrice,
  }), [
    products, catalogSettings, globalSale, featuredProductIds, hallOfFame, faqs, checkoutConfig, siteContent, branding, seoSettings,
    inventory, preorderMessage, customerAccounts, rewardsConfig,
    loading, error, load,
    getProductById, getProductsByCatalogLine, getCatalogProductsForLine, getFeaturedProducts,
    isSaleActive, saleBadgeLabel, showCompareAtPrice,
  ])

  return <ProductsContext.Provider value={value}>{children}</ProductsContext.Provider>
}

export function useProducts() {
  const ctx = useContext(ProductsContext)
  if (!ctx) throw new Error('useProducts must be used within ProductsProvider')
  return ctx
}
